import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Scrollbar } from 'react-scrollbars-custom';
import { constants } from '../../../common/constants/constants';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { getBuildingTitle } from '../../../store/buildings/actions';
import { newSelectStyles } from '../../../common/shared/select.styled';
import PrintPdfIcons from '../../../common/export_documents/PrintPdfIcons';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { getSelectMonthForDropdown } from '../../../common/shared/statelessMethods';
import BllinkUtils from '../../../common/utils/utils';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import Search from '../../../images/search.svg';
import { StyledReport } from '../report.styled';
import { StyledMonthlyExpenses } from './MonthlyExpenses.styled';
import ExpensesCard from './ExpensesCard';
import {CurrencyValue} from "../../../common/currencyValue";

const StringUtils = BllinkUtils.Strings;
const FilterUtils = BllinkUtils.Filter;

const MonthlyExpenses = ({ match, history }) => {
    const { buildingID, year, month } = match.params;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const titleData = useSelector((state) => state.buildings.titleData);
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const pdfContainer = useRef();
    const client = useAccessToken();

    useBodyClass('report');

    const [monthData, setMonthData] = useState({
        monthsTexts: [],
        selectedIndex: 0
    });
    const [expenses, setExpenses] = useState([]);
    const [total, setTotal] = useState({
        amount: '',
        currency: ''
    });
    const [titleText, setTitleText] = useState('');
    const [filter, setFilter] = useState('');

    useEffect(() => {
        (async function () {
            if (!client) return;

            dispatch(showLoader());

            if (!titleData.translationKey) dispatch(getBuildingTitle({buildingID}));

            const monthsTexts = getSelectMonthForDropdown(year, t);
            setMonthData({
                monthsTexts,
                selectedIndex: monthsTexts.findIndex(item => item.value === `${parseInt(month)}_${year}`)
            });

            const { total, items } = await client.managers.expenses.getReport(buildingID, year, month);
            console.log(items);
            setTotal({
                amount: total?.value ?? '-',
                currency: total?.currency
            });
            setExpenses(items);

            dispatch(hideLoader());
        })();
    }, [client]);

    useEffect(() => {
        setTitleText(
            t('expenses.expense_monthly_report', {
                amount: StringUtils.numberWithCommas(total?.amount / 100),
                currency: total?.currency}
            )
        )
    }, [total]);

    /**
     * @param {ExpensesReportItem} expense
     * @param {number} i
     * */
    function renderExpenses(expense, i) {
        const date = BllinkUtils.BllinkTime.parseDate(expense?.reportedPpd?.Payment?.PaymentDate);
        const amount = expense?.amount?.value;
        const paid = expense?.paid?.value;
        const currency = expense?.amount?.currency;
        const vendor = expense?.vendor?.Title;
        const paymentType = expense?.pp?.BuildingPaymentPlan?.ExpenseType?.expenseType;
        const paymentTypeText = paymentType && t(`expenses.expenseTypes.${paymentType}`);
        const frequencyNumber = expense?.pp?.PlanType;
        const frequencyKey = constants.APIContract.expensesByNumbers[frequencyNumber];
        const frequency = t(`expenses.${frequencyKey}`);
        const bankReconsileNumber = expense?.reportedPpd?.Payment?.PaymentState;
        const bankReconsileKey = constants.PaymentPlanDetailsStatus[bankReconsileNumber];
        const bankReconsile = t(`statuses.${bankReconsileKey}`);
        const paymentMethodNumber = expense?.reportedPpd?.Payment?.paymentMethodId;
        const paymentMethodKey = constants.APIContract.paymentMethodsById[paymentMethodNumber].serverKey;
        const paymentMethod = t(`buildings.${paymentMethodKey}`);
        const chequeNumber = expense?.reportedPpd?.Payment?.OfflinePayment?.confirmationID;
        const note = expense?.pp?.Comment;
        const cartID = expense?.pp?.cartID;
        const showItem = FilterUtils.filterItems([date, amount, vendor, paymentTypeText, frequency, bankReconsile, paymentMethod, chequeNumber, note], filter);

        return (
            showItem && (<ExpensesCard
                key={i} 
                date={date}
                amount={amount}
                paid={paid}
                currency={currency}
                vendor={vendor}
                paymentType={paymentTypeText}
                frequency={frequency}
                bankReconsile={bankReconsile}
                paymentMethod={paymentMethod}
                chequeNumber={chequeNumber}
                note={note}
                cartID={cartID}
            />)
        )             
    }

    const onMonthAndYearChanged = async monthYearData => {
        const parts = monthYearData.value.split('_');
        const newMonth = parts[0];
        const newYear = parts[1];
        const selectedIndex = monthData.monthsTexts.findIndex(option => option.value === `${parseInt(newMonth)}_${newYear}`)
        setMonthData({
            ...monthData,
            selectedIndex
        });
        const newUrl = `/${constants.pages.monthlyExpensesUrl}-new/${buildingID}/${newMonth}/${newYear}`
        history.push({ pathname: newUrl });
    };

    return (
        <StyledMonthlyExpenses ltr={ltr}>
            <h1 className='page-title text-only'>
                {t('reports.expenses_monthly_title')}
                {titleData.translationKey && (
                    <span className='building-title'>{t('payments.' + titleData.translationKey, titleData)}</span>
                )}
            </h1>
            <StyledReport className='report' ltr={ltr}>
                <div className='report-wrap'>
                    <div className='report-top'>
                        <div className='report-top-start'>
                            <div className="search">
                                <input
                                    type="text"
                                    name="search"
                                    onChange={e => setFilter(e.target.value.toLowerCase())}
                                />
                                <img src={Search} alt={t('search.search')} />
                            </div>
                            <Select
                                className={'select'}
                                styles={newSelectStyles}
                                options={monthData.monthsTexts}
                                value={monthData.monthsTexts[monthData.selectedIndex]}
                                onChange={onMonthAndYearChanged}
                                isRtl={!ltr}
                            />
                        </div>

                        <div className='report-top-end'>
                            <PrintPdfIcons
                                classes='report-pdf-icons'
                                buildingID={buildingID}
                                eventLabel="apartmentCashflow"
                                titleString={titleText}
                                excelTitle={titleText}
                                reportName={t('reports.expenses_monthly_title')}
                                pdfContainer={pdfContainer.current}
                            />                           
                        </div>
                    </div>
                </div>
            </StyledReport>

            <div id="section-to-print" ref={pdfContainer}>
                <div className='expenses-subtitle'>
                    <span>{t('reports.expenses_monthly_subtitle')}:</span>
                    <CurrencyValue currency={total.currency}>{total.amount}</CurrencyValue>
                </div> 
                <Scrollbar
                    style={{ width: '100%', height: 'calc(100vh - 200px)' }}
                    trackYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-track" />;
                        }
                    }}
                    thumbYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                        }
                    }}
                    rtl={false}
                    noScrollX
                >
                    <div className='expenses-cards-wrapper'>
                        {expenses.length === 0 && <div className='no_records'>{t('expenses.bank_adjustments.no_records')}</div>}
                        {expenses.map((expense, i) => renderExpenses(expense, i))}
                    </div>                  
                </Scrollbar>
            </div>
        </StyledMonthlyExpenses>
    );
};

export default MonthlyExpenses;
